import React, { useEffect, useState, useRef } from 'react'
import { AxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { ErrorType, LoginStorageData } from 'src/shared/types/src';
import { setNewPassword } from 'src/shared/services/src';
import { getLoginStoreData, validatePassword } from 'src/shared/utils/src';
import { useAppDispatch, store } from 'src/app/services/store';
import { performAfterLogin } from 'src/shared/redux-state/src';
import Footer from 'src/app/components/commonComponents/Footer';
import Images from '../../static'
import CustomButton from 'src/app/components/commonComponents/CustomButton';
import CustomTextInput from 'src/app/components/commonComponents/CustomTextInput';
import { Eye, EyeOff } from 'react-ionicons';
import DownloadAppComponent from 'src/app/components/commonComponents/DownloadAppComponent';
import { AppRoutes } from 'src/app/navigation/routes';
import { APP_MESSAGES } from 'src/shared/config/src';
import { Progress } from 'reactstrap';
import { getRedirectionRoute } from 'src/app/utilities/helpers';
import { URL_CONSTANTS } from 'src/app/config/appConstants';
// import { performAfterLogin } from '@parent-app/redux-state';
// import { ErrorType, LoginStorageData } from '@parent-app/types';
// import { getLoginStoreData, validatePassword } from '@parent-app/utils';
// import { useAppDispatch, store } from '@eyparent-web/services/store';
// import { setNewPassword } from '@parent-app/services';

const FIELD_MAP = {
  PASSWORD: 'password',
  CONFIRM_PASSWORD: 'confirmPassword'
};

const SetPasswordScreen = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const inputRef: any = useRef(null);
  const buttonRef: any = useRef(null);
  const { email, sessionId, dob, otp } = location.state || {};

  const apiErrorMap: { [key: string]: string } = {
    "password": FIELD_MAP.PASSWORD,
    "confirmPassword": FIELD_MAP.CONFIRM_PASSWORD
  }

  const [errors, setErrors] = useState<ErrorType>({});
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPasswordValid, setPasswordValid] = useState<boolean>(false);
  const [passwordValidateRules, setPasswordValidateRules] = useState<{ description: string, name: string }[]>([]);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [redirectionRoute, setRedirectionRoute] = useState<string>();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    const redirectTo = getRedirectionRoute();
    if (redirectTo) {
      setRedirectionRoute(redirectTo);
    }
  }, []);

  useEffect(() => {
    if (!email) {
      navigate(AppRoutes.VERIFY_EMAIL);
    }
  }, [email])

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter' && buttonRef.current) {
      buttonRef.current.click();
    }
  };

  const runValidations = () => {
    let errorObj: ErrorType = {};
    if (!password) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.PASSWORD]: {
          isErrored: true,
          errorMsg: `Password cannot be blank`,
        },
      };
    } else if (!confirmPassword) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.CONFIRM_PASSWORD]: {
          isErrored: true,
          errorMsg: `Confirm password cannot be blank`,
        },
      };
    }
    if (password !== confirmPassword) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.CONFIRM_PASSWORD]: {
          isErrored: true,
          errorMsg: `Passwords do not match`,
        },
      };
    }
    setErrors(errorObj);

    if (Object.keys(errorObj).length) return false;
    else return true;
  }

  const handleSetPassword = async () => {
    if (isLoading) return;

    let validity = runValidations();
    if (!validity) return;

    if (!isPasswordValid || passwordValidateRules.length > 0) return;

    try {
      setErrors({});
      setIsLoading(true);
      const response = await setNewPassword({ email, sessionId, dob, otp, password, confirmPassword });
      if (response && Object.keys(response).length > 0) {
        const dataToStore: LoginStorageData | undefined = getLoginStoreData(response);
        if (dataToStore) {
          await dispatch(performAfterLogin({ dataToStore, reduxStore: store }));
          dataToStore.termsAccepted ? 
            (redirectionRoute ? navigate(redirectionRoute) : navigate(AppRoutes.HOME)) : 
            navigate(`${AppRoutes.TERMS_CONDITIONS}${redirectionRoute ? `?${URL_CONSTANTS.REDIRECT_TO}=${redirectionRoute}` : ''}`);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const apiError = error as AxiosError;
        if (apiError && apiError.response && apiError.response.status === 404) {
          const { errors } = error.response?.data;
          toast.error(errors.join(", "), { autoClose: 5000 });
        } else if (apiError && apiError.response && apiError.response.status === 400) {
          // validation errors
          const { validation_errors, errors } = error.response?.data;
          let errorObj: ErrorType = {};
          if (validation_errors && Object.keys(validation_errors).length > 0) {
            for (let [key, value] of Object.entries(validation_errors)) {
              errorObj = {
                ...errorObj,
                [apiErrorMap[key]]: {
                  isErrored: true,
                  errorMsg: (value as any[]).length > 1 ? (value as any[]).join(",") : (value as any[])[0]
                }
              }
            }
            setErrors(errorObj);
          } else if (errors && Object.keys(errors).length > 0) {
            toast.error(errors.join(", "), { autoClose: 5000 });
          }
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  const onPasswordChange = (e: any) => {
    let errorObj: ErrorType = errors;
    const userInput = e.target.value
    errorObj = {
      ...errorObj,
      [FIELD_MAP.PASSWORD]: {
        isErrored: false,
        errorMsg: ``,
      },
    };
    setErrors(errorObj);
    setPassword(userInput);
    const validateRules: any = validatePassword(userInput);
    setPasswordValidateRules(validateRules);
    if (!validateRules.length) {
      setPasswordValid(true);
    } else setPasswordValid(false);
  }

  const onConfirmPasswordChange = (e: any) => {
    let errorObj: ErrorType = errors;
    const userInput = e.target.value
    if (!userInput) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.CONFIRM_PASSWORD]: {
          isErrored: false,
          errorMsg: ``,
        },
      };
    }
    if (password !== userInput) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.CONFIRM_PASSWORD]: {
          isErrored: true,
          errorMsg: `Passwords do not match`,
        },
      };
      setPasswordValid(false);
    } else {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.CONFIRM_PASSWORD]: {
          isErrored: false,
          errorMsg: ``,
        },
      };
      setPasswordValid(true);
    }
    setErrors(errorObj);
    setConfirmPassword(userInput);
  }

  const strengthValue = (val: number) => {
    switch (val) {
      case 1: {
          return "Very Poor"
      }
      case 2: {
        return "Poor"
      }
      case 3: {
        return "Fair"
      }
      case 4: {
        return "Good"
      }
      case 5: {
        return "Strong"
      }
    }
  }

  const strengthColor = (val: number) => {  
    switch (val) {
      case 0: {
        return "danger"
      }
      case 1: {
        return "danger"
      }
      case 2: {
        return "warning"
      }
      case 3: {
        return "warning"
      }
      case 4: {
        return "warning"
      }
      default: {
        return "success"
      }
    }
  }

  return (
    <div className='main-wrapper bg-white container'>
      <div className='d-flex flex-column flex-grow-1 mg-b-20'>
        <div className='mg-b-78 mg-t-xxl-100 mg-t-xs-50 pd-t-md-30 pd-t-xs-0 d-flex justify-content-center align-items-center'>
          <img src={Images.logo} alt="logo" className="eyworks-logo" />
        </div>
        <p className='fs-4 text-black fw-bold'>Create New Password</p>
        <p className='fs-14 text-black mg-b-28'>Your password must be at least 8 characters long and include 1 number, 1 special character (!@#$%^&*_+) 1 upper case and 1 lowercase character.</p>

        <div className='mg-b-25'>
          <CustomTextInput
            type={showPassword ? 'text' : 'password'}
            name={'password'}
            value={password}
            onChange={onPasswordChange}
            label={'New Password'}
            icon={showPassword ? <Eye
              color={'rgba(0, 0, 0, 0.20)'}
              height="20px"
              width="20px"
            /> : <EyeOff
              color={'rgba(0, 0, 0, 0.20)'}
              height="20px"
              width="20px"
            />}
            isIconClick={() => setShowPassword(!showPassword)}
            handleKeyDown={() => handleKeyDown}
            inputRef={inputRef}
          />
          {password ? 
            <div className="password-strength-bar">
              <Progress
                max={5}
                value={passwordValidateRules && passwordValidateRules.length ? 5 - passwordValidateRules.length : 5}
                color={passwordValidateRules && passwordValidateRules.length ? 
                  strengthColor(5 - passwordValidateRules.length) 
                    : 
                  'success'
                }
              />
              <span className='password-score-word'>{strengthValue(5 - passwordValidateRules.length)}</span>
            </div> 
            : 
            null
          }
          {/* { password ? 
            <PasswordStrengthBar 
              password={password} 
              className='password-strength-bar' 
              scoreWordClassName='password-score-word' 
              minLength={8}
            /> : null
          } */}
          {errors && errors[FIELD_MAP.PASSWORD] && errors[FIELD_MAP.PASSWORD].isErrored ? 
            <span className="form-error">{errors[FIELD_MAP.PASSWORD].errorMsg}</span> 
            : null
          }

          {(passwordValidateRules && passwordValidateRules.length) ? (
            <div>
              {passwordValidateRules.map((rule, index) => (
                <span className="form-error" key={index}>{rule.description}</span>
              ))}
            </div>
          ) : null}

        </div>
        
        <div className='mg-b-35'>
          <CustomTextInput
            type={showConfirmPassword ? 'text' : 'password'}
            name={'confirmPassword'}
            value={confirmPassword}
            onChange={onConfirmPasswordChange}
            label={'Confirm New Password'}
            icon={showConfirmPassword ? <Eye
              color={'rgba(0, 0, 0, 0.20)'}
              height="20px"
              width="20px"
            /> : <EyeOff
              color={'rgba(0, 0, 0, 0.20)'}
              height="20px"
              width="20px"
            />}
            isIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
            handleKeyDown={() => handleKeyDown}
          />
          {errors && errors[FIELD_MAP.CONFIRM_PASSWORD] && errors[FIELD_MAP.CONFIRM_PASSWORD].isErrored ? <span className="form-error">{errors[FIELD_MAP.CONFIRM_PASSWORD].errorMsg}</span> : null}
        </div>
       
        <CustomButton buttonRef={buttonRef} text='Submit' onClick={handleSetPassword} color='primary' isLoading={isLoading} />
      </div>
      <div className='mg-b-10'>
        <DownloadAppComponent />
        <Footer />
      </div>
    </div>
  )
}

export default SetPasswordScreen;